import { useEffect, useRef } from 'react';
import { css } from 'styled-system/css';
import type { ReactZoomPanPinchContentRef } from 'react-zoom-pan-pinch';
import type { Puzzle } from '@/domain';
import { GridMinimap } from '@/ui/components/grid/GridMinimap';
import type { FocusedCell } from '@/ui/components/grid/focusedCell';
import type { Clue, Direction } from '@/ui/components/grid/useGridNavigation';
import { HintIcon } from '@/ui/components/icons';
import { t } from '@/ui/i18n';
import { AZERTY_ROWS } from './azertyLayout';
import { ClueBanner } from './ClueBanner';
import { DirectionArrowIcon } from './DirectionArrowIcon';
import { KeyboardKey } from './KeyboardKey';
import { TabKeyIcon } from './TabKeyIcon';

// touchAction: 'pan-y' suppresses pinch while preserving vertical pan (pull-to-refresh) — ADR-0016 keyboard-mounted exception.
const panel = css({
  position: 'fixed',
  insetInline: 0,
  insetBlockEnd: 0,
  bg: 'surface',
  borderTop: '1px solid token(colors.border)',
  paddingInline: '6px',
  paddingBlockStart: '8px',
  paddingBlockEnd: 'calc(env(safe-area-inset-bottom) + 16px)',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  zIndex: 20,
  touchAction: 'pan-y',
});

const row = css({
  display: 'flex',
  gap: '4px',
  justifyContent: 'center',
});

// 6x2 action block: minimap spans cols 1-3 rows 1-2; right half holds Prev/Up/Next then Left/Down/Right.
const navBlock = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gridTemplateRows: 'repeat(2, 44px)',
  gap: '4px',
  paddingBottom: '4px',
  borderBottom: '1px dashed token(colors.border)',
  marginBottom: '4px',
});

// Each cell is its own flex shell so KeyboardKey's `flex: 1` fills the track.
const navCell = css({ display: 'flex', minWidth: 0 });

// Minimap occupies the left half (cols 1-3, both rows).
const minimapCell = css({
  display: 'flex',
  minWidth: 0,
  gridColumn: '1 / span 3',
  gridRow: '1 / span 2',
});

// Icon-only hint glyph; count stays accessible via the button's aria-label.
const hintIconStyles = css({
  display: 'inline-flex',
  width: '20px',
  height: '20px',
  '& svg': { width: '100%', height: '100%' },
});

export interface MobileKeyboardProps {
  readonly onLetter: (char: string) => void;
  readonly onBackspace: () => void;
  readonly onToggleDirection: () => void;
  readonly onPrevClue: () => void;
  readonly onNextClue: () => void;
  readonly onRequestHint: () => void;
  // Cursor step; flip-then-step semantics match the physical arrow keys.
  readonly onMoveCursor: (direction: 'left' | 'right' | 'up' | 'down') => void;
  readonly activeClue: Clue | null;
  readonly alternateClue: Clue | null;
  readonly hintRemaining: number;
  readonly hintExhausted: boolean;
  readonly hintPending: boolean;
  // Imperative read at click time (ADR-0002 §4).
  readonly getFocusedCell: () => FocusedCell | null;
  // Reads cell entries for the banner letter-preview row; identity bumps per write per ADR-0002 §4.
  readonly getEntryAt: (row: number, col: number) => string;
  // The local user's focused cell — drives the rose underline on the active-clue letter preview.
  readonly focusedPosition: { row: number; col: number } | null;
  // Validation-set predicate; absent means no cell is validated.
  readonly isCellValidated?: (row: number, col: number) => boolean;
  readonly puzzle: Puzzle;
  readonly validatedPositions: ReadonlySet<string>;
  readonly filledPositions?: ReadonlySet<string>;
  readonly currentWordKeys?: ReadonlySet<string>;
  readonly localCursor?: { position: { row: number; col: number }; direction: Direction } | null;
  readonly transformRef: React.RefObject<ReactZoomPanPinchContentRef | null>;
  readonly scale: number;
  readonly positionX: number;
  readonly positionY: number;
  readonly contentWidth: number;
  readonly contentHeight: number;
}

export function MobileKeyboard(props: MobileKeyboardProps) {
  const {
    onLetter,
    onBackspace,
    onToggleDirection,
    onPrevClue,
    onNextClue,
    onRequestHint,
    onMoveCursor,
    activeClue,
    alternateClue,
    hintRemaining,
    hintExhausted,
    hintPending,
    getFocusedCell,
    getEntryAt,
    focusedPosition,
    isCellValidated,
    puzzle,
    validatedPositions,
    filledPositions,
    currentWordKeys,
    localCursor,
    transformRef,
    scale,
    positionX,
    positionY,
    contentWidth,
    contentHeight,
  } = props;

  // One-shot reset of locked visual-viewport zoom; reverts within one frame to honor ADR-0016 §2.
  useEffect(() => {
    const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    if (!viewport) return;
    const initial = viewport.getAttribute('content') ?? '';
    viewport.setAttribute('content', `${initial}, maximum-scale=1`);
    const raf = requestAnimationFrame(() => {
      viewport.setAttribute('content', initial);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    // Publish initial height before ResizeObserver fires so consumers reserve space correctly.
    const publish = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--mobile-kb-height', `${h}px`);
    };
    publish();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    return () => {
      ro?.disconnect();
      document.documentElement.style.removeProperty('--mobile-kb-height');
    };
  }, []);

  // Click-time guard preserves uncontrolled-input contract (ADR-0002 §4).
  const handleRequestHint = () => {
    const cell = getFocusedCell();
    if (!cell || cell.isLocked) return;
    onRequestHint();
  };

  const hintDisabled = hintExhausted || hintPending || activeClue === null;
  const lettersInert = activeClue === null;

  return (
    <div ref={panelRef} className={panel} role="group" aria-label={t('keyboard.aria.panel')}>
      <ClueBanner
        clue={activeClue}
        alternateClue={alternateClue}
        onToggleDirection={onToggleDirection}
        getEntryAt={getEntryAt}
        focusedPosition={focusedPosition}
        isCellValidated={isCellValidated}
      />
      <div className={navBlock}>
        <div className={minimapCell}>
          <GridMinimap
            variant="panel"
            puzzle={puzzle}
            validatedPositions={validatedPositions}
            filledPositions={filledPositions}
            currentWordKeys={currentWordKeys}
            localCursor={localCursor}
            transformRef={transformRef}
            scale={scale}
            positionX={positionX}
            positionY={positionY}
            contentWidth={contentWidth}
            contentHeight={contentHeight}
          />
        </div>
        <div className={navCell}>
          <KeyboardKey
            label={<TabKeyIcon direction="previous" />}
            ariaLabel={t('clueRail.aria.prev')}
            variant="action"
            onPress={onPrevClue}
          />
        </div>
        <div className={navCell}>
          <KeyboardKey
            label={<DirectionArrowIcon direction="up" />}
            ariaLabel={t('keyboard.aria.cursorUp')}
            variant="action"
            onPress={() => onMoveCursor('up')}
          />
        </div>
        <div className={navCell}>
          <KeyboardKey
            label={<TabKeyIcon direction="next" />}
            ariaLabel={t('clueRail.aria.next')}
            variant="action"
            onPress={onNextClue}
          />
        </div>
        <div className={navCell}>
          <KeyboardKey
            label={<DirectionArrowIcon direction="left" />}
            ariaLabel={t('keyboard.aria.cursorLeft')}
            variant="action"
            onPress={() => onMoveCursor('left')}
          />
        </div>
        <div className={navCell}>
          <KeyboardKey
            label={<DirectionArrowIcon direction="down" />}
            ariaLabel={t('keyboard.aria.cursorDown')}
            variant="action"
            onPress={() => onMoveCursor('down')}
          />
        </div>
        <div className={navCell}>
          <KeyboardKey
            label={<DirectionArrowIcon direction="right" />}
            ariaLabel={t('keyboard.aria.cursorRight')}
            variant="action"
            onPress={() => onMoveCursor('right')}
          />
        </div>
      </div>
      {AZERTY_ROWS.map((letters, rowIdx) => (
        <div key={rowIdx} className={row}>
          {letters.map((ch) => (
            <KeyboardKey
              key={ch}
              label={ch}
              ariaLabel={t('keyboard.aria.letter', { letter: ch })}
              disabled={lettersInert}
              onPress={() => onLetter(ch)}
            />
          ))}
          {rowIdx === AZERTY_ROWS.length - 1 ? (
            <>
              <KeyboardKey
                label={
                  <span className={hintIconStyles}>
                    <HintIcon />
                  </span>
                }
                ariaLabel={t('keyboard.aria.hint', { remaining: hintRemaining })}
                variant="action"
                disabled={hintDisabled}
                onPress={handleRequestHint}
              />
              <KeyboardKey
                label="⌫"
                ariaLabel={t('keyboard.aria.backspace')}
                variant="action"
                onPress={onBackspace}
              />
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}
